import { Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronDownIcon } from '../../assets/bot-icons/chevron-down-24.svg';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat';
import { AlertType } from '../../libs/models/AlertType';
import { ChatMessageType, hasAgentJoinedOrResolved, isAgentSessionActive } from '../../libs/models/ChatMessage';
import { IAskImage } from '../../libs/semantic-kernel/model/Ask';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';
import { editConversationInput, updateBotResponseStatus } from '../../redux/features/conversations/conversationsSlice';
import { SharedStyles } from '../../styles';
import { ChatInput } from './ChatInput';
import { ChatHistory } from './chat-history/ChatHistory';
import { GuidanceCard } from './guidance-card/GuidanceCard';
import { ChatState } from '../../redux/features/conversations/ChatState';
import { EscalationOfferEntry } from '../../redux/features/livechat/escalationOfferSlice';
import { classifyAppend, isNearBottom, isScrolledToBottom } from './chatScrollUtils';

// Fallback duration for clearing the programmatic-scroll flag when `scrollend`
// doesn't fire (older Safari, or scrolls short enough that no animation runs).
// Generous on purpose — on supported browsers the scrollend listener cancels
// this timer long before it expires.
const SCROLLEND_FALLBACK_MS = 1000;

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        width: '100%',
        position: 'relative',
    },
    scroll: {
        margin: `${tokens.spacingVerticalXS} 0`,
        padding: `0 ${tokens.spacingHorizontalS}`,
        ...SharedStyles.scroll,
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
    },
    scrollEmbedded: {
        margin: `${tokens.spacingVerticalXS} 0`,
        padding: `0 ${tokens.spacingHorizontalS}`,
        ...SharedStyles.scroll,
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
    },
    scrollContent: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    spacer: {
        flexGrow: 1,
    },
    guidanceCard: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
    },
    history: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS} ${tokens.spacingVerticalM} ${tokens.spacingHorizontalXXL}`,
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        boxSizing: 'border-box',
    },
    historyEmbedded: {
        padding: `${tokens.spacingVerticalM} 0 ${tokens.spacingVerticalM} ${tokens.spacingHorizontalS}`,
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        boxSizing: 'border-box',
    },
    input: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
    },
    inputEmbedded: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXL}`,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
    },
    jumpToBottom: {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: '100%',
        marginBottom: tokens.spacingVerticalS,
        zIndex: 1,
        boxShadow: tokens.shadow8,
        backgroundColor: 'var(--app-nav-tab-hover-background)',
        ':hover': {
            backgroundColor: 'var(--app-nav-tab-active-background)',
        },
        '& svg': {
            display: 'block',
            transform: 'translateY(1px)',
        },
    },
    // Accent "new messages" variant of the control: when messages have arrived
    // below the fold it becomes a labelled pill that stands out, so the reader
    // notices instead of the chevron sitting there looking idle.
    jumpToBottomUnread: {
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
        ':hover': {
            backgroundColor: tokens.colorBrandBackgroundHover,
            color: tokens.colorNeutralForegroundOnBrand,
        },
    },
});

export const ChatRoom: React.FC = () => {
    // --- Hooks must be called unconditionally at the top ---
    const classes = useClasses();
    const { t } = useTranslation();
    const chat = useChat();
    const dispatch = useAppDispatch();
    const scrollViewTargetRef = React.useRef<HTMLDivElement>(null);
    const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);
    // Count of messages that arrived while the reader was scrolled up in the
    // backlog (live chat / human handover, where the agent can send several
    // messages in a row). Surfaced on the jump-to-bottom control so a new message
    // is never silently below the fold; reset to 0 once the reader is back at the
    // bottom. Stays 0 for the AI assistant, whose replies stream under
    // botResponseStatus and so never take the discrete-append path below.
    const [unreadCount, setUnreadCount] = React.useState(0);

    // --- Selectors ---
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { embeddedMode } = useAppSelector((state: RootState) => state.app);
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);

    // Get the selected conversation safely
    const selectedConversation = conversations[selectedId] as ChatState | undefined;
    // Existence flag (not the object) for the scroll-listener effect's dependency: re-run
    // it when a conversation first loads so the listener attaches to the now-rendered
    // container, without churning on every message that mutates selectedConversation.
    const hasConversation = Boolean(selectedConversation);
    const botResponseStatus = selectedConversation?.botResponseStatus;
    const messageCount = selectedConversation?.messages.length ?? 0;
    // Once a human agent has joined, the "land at the top of the latest bot answer" scroll below
    // must not fire: the last bot message is the stale pre-handoff AI answer, and sending a message
    // to the agent (which optimistically sets botResponseStatus) would otherwise yank the view up
    // to it instead of staying at the bottom like a normal messenger thread (#1769).
    const agentHandoff = React.useMemo(
        () => hasAgentJoinedOrResolved(selectedConversation?.messages ?? []),
        [selectedConversation?.messages],
    );
    // Distinct from agentHandoff above: this one flips back to false once the agent resolves the
    // chat, so AI generating-state returns when the customer talks to the assistant again (#1770).
    const agentSessionActive = React.useMemo(
        () => isAgentSessionActive(selectedConversation?.messages ?? []),
        [selectedConversation?.messages],
    );
    // The escalation offer card appears asynchronously and isn't a message, so the message-count
    // auto-follow below never sees it. Track its presence separately to scroll it into view.
    const offerPresent = useAppSelector((state: RootState) => {
        const offer = state.escalationOffer.offers[selectedId] as EscalationOfferEntry | undefined;
        return !!offer && !offer.dismissed;
    });
    const prevStatusRef = React.useRef<string | undefined>(undefined);
    const prevSelectedIdRef = React.useRef(selectedId);
    const prevMessageCountRef = React.useRef(messageCount);
    const messageCountChatIdRef = React.useRef(selectedId);
    const prevOfferPresentRef = React.useRef(offerPresent);
    const offerPresentChatIdRef = React.useRef(selectedId);
    const programmaticScrollRef = React.useRef(false);
    const programmaticScrollTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const programmaticScrollClearRef = React.useRef<(() => void) | null>(null);
    // True while a "pin to the bottom" smooth scroll is in flight. The append effect
    // treats this as "at the bottom" so a fast series of messages keeps following down
    // (a message arriving mid-animation reads a position far from the freshly grown
    // bottom and would otherwise be misclassified as unread). Cleared when it settles.
    const pinToBottomActiveRef = React.useRef(false);
    // Whether the reader was near the bottom as of the last settled scroll position —
    // i.e. *before* any pending message was appended. The append decision must use this
    // latched value, not a live measurement: by the time the effect runs the new message
    // is already in the DOM, so a message taller than the near-bottom band would push the
    // live position out of range and be misread as "unread" even though the reader was
    // caught up. Starts true (a fresh conversation lands at the bottom).
    const nearBottomRef = React.useRef(true);

    // Mark the reader as caught up with the live bottom: re-arm streaming auto-follow,
    // clear the unread badge, and latch the position so the next append follows rather
    // than counting as unread. Used by every "we're at the bottom now" site (jump button,
    // submit, chat switch, append-follow) so they can't drift out of lockstep.
    const markCaughtUp = React.useCallback(() => {
        setShouldAutoScroll(true);
        setUnreadCount(0);
        nearBottomRef.current = true;
    }, []);

    // Tear down any in-flight programmatic scroll's bookkeeping — the scrollend
    // listener, the fallback timer, and the suppress/pin flags — WITHOUT reconciling
    // scroll state (that's clear()'s job; the caller here decides where the view lands).
    // Used both before starting a new programmatic scroll and on chat switch, where the
    // previous chat's pin animation must not leak its pinToBottomActiveRef into the new
    // chat's append decisions (the scroll container is the same DOM node across switches).
    const cancelProgrammaticScroll = React.useCallback(() => {
        const container = scrollViewTargetRef.current;
        if (programmaticScrollClearRef.current && container) {
            container.removeEventListener('scrollend', programmaticScrollClearRef.current);
        }
        programmaticScrollClearRef.current = null;
        if (programmaticScrollTimerRef.current) {
            clearTimeout(programmaticScrollTimerRef.current);
            programmaticScrollTimerRef.current = null;
        }
        programmaticScrollRef.current = false;
        pinToBottomActiveRef.current = false;
    }, []);

    // Suppress the scroll listener while a programmatic smooth scroll is in flight,
    // so its intermediate positions don't toggle shouldAutoScroll mid-animation
    // (which would flicker the jump-to-bottom button). Clears on the `scrollend`
    // event so suppression ends exactly when the animation does; SCROLLEND_FALLBACK_MS
    // is the safety-net timeout for browsers without scrollend support.
    //
    // `pinToBottom` marks scrolls whose intent is "land at the very bottom and stay
    // there" (jump-to-bottom button, append/offer auto-follow) — as opposed to the
    // streaming-end scroll, which intentionally lands at the *top* of the last bot
    // answer. See clear() for why the distinction matters when content grows mid-scroll.
    const beginProgrammaticScroll = React.useCallback(
        (pinToBottom = false) => {
            const container = scrollViewTargetRef.current;
            if (!container) return;

            // Cancel any in-flight programmatic scroll's bookkeeping before starting a new
            // one, so a stale scrollend listener can't fire a second, conflicting reconcile
            // (these stack when messages arrive faster than the smooth animation settles).
            cancelProgrammaticScroll();
            programmaticScrollRef.current = true;
            pinToBottomActiveRef.current = pinToBottom;

            const clear = () => {
                programmaticScrollRef.current = false;
                pinToBottomActiveRef.current = false;
                if (programmaticScrollTimerRef.current) {
                    clearTimeout(programmaticScrollTimerRef.current);
                    programmaticScrollTimerRef.current = null;
                }
                container.removeEventListener('scrollend', clear);
                programmaticScrollClearRef.current = null;
                // Reconcile auto-scroll state with the settled position: scroll events
                // fired during the animation were suppressed, so sample the post-animation
                // truth here. Without this, a programmatic scroll that lands at the bottom
                // (or doesn't move at all because content already fits) leaves the
                // jump-to-bottom button stuck visible.
                //
                // When the intent was to pin to the bottom but we settled above it, the
                // content grew while the smooth animation was running (e.g. several agent
                // messages streamed in during a human handover), so the animation undershot
                // the now-taller content. Snap to the live bottom instantly and stay pinned
                // instead of surrendering — otherwise shouldAutoScroll flips to false, the
                // jump-to-bottom button reappears, and the append auto-follow effect (gated
                // on shouldAutoScroll) stops following the remaining messages (#1917).
                let atBottom = isScrolledToBottom(container);
                if (pinToBottom && !atBottom) {
                    container.scrollTo(0, container.scrollHeight);
                    atBottom = true;
                }
                setShouldAutoScroll(atBottom);
                if (atBottom) setUnreadCount(0);
                // Refresh the latch from the settled position (the scroll listener is
                // suppressed during the animation, so it didn't track these positions).
                nearBottomRef.current = isNearBottom(container);
            };

            programmaticScrollClearRef.current = clear;
            container.addEventListener('scrollend', clear, { once: true });
            programmaticScrollTimerRef.current = setTimeout(clear, SCROLLEND_FALLBACK_MS);
        },
        [cancelProgrammaticScroll],
    );

    // --- Effects (Must be called unconditionally AFTER basic hooks/selectors) ---
    // Auto-scroll only while a response is streaming, so post-completion content
    // (e.g. follow-up suggestions) doesn't yank the view back to the bottom.
    React.useEffect(() => {
        if (!shouldAutoScroll) return;
        if (!botResponseStatus) return;
        scrollViewTargetRef.current?.scrollTo(0, scrollViewTargetRef.current.scrollHeight);
    }, [selectedConversation, shouldAutoScroll, botResponseStatus]);

    // On streaming -> idle transition, land at the top of the latest bot message
    // and stop auto-scrolling so late-arriving content (suggestions, metadata)
    // doesn't move the view. The backend signals end-of-stream by clearing
    // botResponseStatus to undefined (ChatController.cs ~L214), so any truthy
    // -> falsy transition counts. On chat switch, reset to the bottom so the
    // new conversation isn't left at a stale scroll position from the previous one.
    React.useEffect(() => {
        const wasStreaming = Boolean(prevStatusRef.current);
        const isStreaming = Boolean(botResponseStatus);
        const chatChanged = prevSelectedIdRef.current !== selectedId;

        prevStatusRef.current = botResponseStatus;
        prevSelectedIdRef.current = selectedId;

        if (chatChanged) {
            // Drop any pin animation still in flight from the previous chat before we
            // jump the (shared) container to the new chat's bottom — otherwise its
            // pinToBottomActiveRef would survive the switch and a message arriving in
            // the new chat within the animation window would be misclassified as
            // 'follow', yanking a reader who is up in the new chat's backlog.
            cancelProgrammaticScroll();
            markCaughtUp();
            scrollViewTargetRef.current?.scrollTo(0, scrollViewTargetRef.current.scrollHeight);
            return;
        }

        // Skip in agent-handoff mode: there's no new bot answer to land on, only the stale
        // pre-handoff one, so scrolling to it would pull the view away from the agent thread.
        if (wasStreaming && !isStreaming && !agentHandoff) {
            const last = scrollViewTargetRef.current?.querySelector<HTMLElement>('[data-last-bot-message="true"]');
            if (!last) return;
            beginProgrammaticScroll();
            last.scrollIntoView({ block: 'start', behavior: 'smooth' });
            // shouldAutoScroll is reconciled inside beginProgrammaticScroll's clear()
            // once the scroll settles. Don't pre-set it false — when the message and
            // follow-up suggestions already fit in the viewport, scrollIntoView is a
            // no-op and the user is still at the bottom.
        }
    }, [botResponseStatus, selectedId, agentHandoff, markCaughtUp, beginProgrammaticScroll, cancelProgrammaticScroll]);

    // Live-chat agent replies arrive over SignalR without a botResponseStatus, so the
    // streaming effect above never scrolls for them. Decide per-append from the live
    // scroll position (not shouldAutoScroll, which is the tight bottom-lock used for
    // streaming): if the reader is roughly caught up, follow the message down; if they're
    // up in the backlog, leave the view put and bump the unread count so the new message
    // is surfaced on the jump-to-bottom control instead of landing silently below the fold.
    React.useEffect(() => {
        const prevCount = prevMessageCountRef.current;
        const chatChanged = messageCountChatIdRef.current !== selectedId;

        prevMessageCountRef.current = messageCount;
        messageCountChatIdRef.current = selectedId;

        const container = scrollViewTargetRef.current;
        // Use the latched pre-append position, not a live read (the new message is already
        // in the DOM here, so a tall one would otherwise push us out of the near-bottom band
        // and be misread as unread). A pin-to-bottom scroll already in flight also counts as
        // "at the bottom" so a fast series keeps following instead of flashing a stray badge.
        const nearBottom = pinToBottomActiveRef.current || nearBottomRef.current;
        const outcome = classifyAppend({
            prevCount,
            messageCount,
            chatChanged,
            isStreaming: Boolean(botResponseStatus),
            nearBottom,
        });

        if (outcome === 'ignore' || !container) return;

        if (outcome === 'follow') {
            markCaughtUp();
            beginProgrammaticScroll(true);
            container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        } else {
            // 'unread' — reader is in the backlog; don't move them, just count what arrived.
            setUnreadCount((count) => count + (messageCount - prevCount));
        }
    }, [messageCount, selectedId, botResponseStatus, markCaughtUp, beginProgrammaticScroll]);

    // The escalation offer card appends below the last message without bumping messageCount, so
    // the effect above never reveals it and it lands below the fold. Follow it down on its first
    // appearance when the reader is roughly caught up. It's a card, not a message, so it doesn't
    // feed the unread count — if the reader is up in the backlog it just stays below the fold.
    React.useEffect(() => {
        const wasPresent = prevOfferPresentRef.current;
        const chatChanged = offerPresentChatIdRef.current !== selectedId;

        prevOfferPresentRef.current = offerPresent;
        offerPresentChatIdRef.current = selectedId;

        if (chatChanged) return;

        if (!wasPresent && offerPresent && !botResponseStatus) {
            const container = scrollViewTargetRef.current;
            // Latched pre-append position — the card is already in the DOM here, so a live
            // read would misjudge a tall card the same way the message path would. A pin
            // scroll already in flight counts as "at the bottom" too (same as the append
            // path): the listener is suppressed during it, so nearBottomRef is stale and
            // would otherwise leave the offer below the fold mid-follow.
            const nearBottom = pinToBottomActiveRef.current || nearBottomRef.current;
            if (!container || !nearBottom) return;
            // markCaughtUp() (not a bare setShouldAutoScroll) keeps shouldAutoScroll,
            // unreadCount and nearBottomRef in lockstep like every other caught-up site,
            // so the pill can't briefly flash a stale count if this scroll is short
            // enough not to emit scrollend before clear()'s reconcile runs.
            markCaughtUp();
            beginProgrammaticScroll(true);
            container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
    }, [offerPresent, selectedId, botResponseStatus, markCaughtUp, beginProgrammaticScroll]);

    React.useEffect(() => {
        const onScroll = () => {
            if (programmaticScrollRef.current) return;
            const container = scrollViewTargetRef.current;
            if (!container) return;
            // Latch the live position so the append/offer effects can read where the reader
            // was *before* the next message lands in the DOM.
            nearBottomRef.current = isNearBottom(container);
            const atBottom = isScrolledToBottom(container);
            setShouldAutoScroll(atBottom);
            // Reaching the bottom means the reader has caught up — clear the unread badge.
            if (atBottom) setUnreadCount(0);
        };

        const currentScrollViewTarget = scrollViewTargetRef.current;
        if (!currentScrollViewTarget) return;

        currentScrollViewTarget.addEventListener('scroll', onScroll);
        return () => {
            currentScrollViewTarget.removeEventListener('scroll', onScroll);
            if (programmaticScrollClearRef.current) {
                currentScrollViewTarget.removeEventListener('scrollend', programmaticScrollClearRef.current);
                programmaticScrollClearRef.current = null;
            }
            if (programmaticScrollTimerRef.current) clearTimeout(programmaticScrollTimerRef.current);
        };
        // Keyed on whether a conversation exists, not just mount: when ChatRoom first
        // renders without a selected conversation it returns null (below), so the scroll
        // container isn't in the DOM and scrollViewTargetRef.current is null. Re-running
        // once the conversation loads attaches the listener to the now-rendered container.
    }, [hasConversation]);

    // --- Early return check (AFTER hooks) ---
    // Return null or a loading indicator if the selected conversation doesn't exist yet
    if (!selectedConversation) {
        return null;
    }

    // --- Derived state (can safely access selectedConversation now) ---
    const statuses = selectedConversation.botResponseStatusHistory;

    // --- Event Handlers ---
    const handleQuestionButtonClick = (text: string) => {
        dispatch(editConversationInput({ id: selectedId, newInput: text }));
    };

    const onSubmit = async (options: GetResponseOptions) => {
        options.hostContext = hostContext;
        await chat.getResponse(options);
    };

    const handleJumpToBottom = () => {
        markCaughtUp();
        if (!scrollViewTargetRef.current) return;
        beginProgrammaticScroll(true);
        scrollViewTargetRef.current.scrollTo({
            top: scrollViewTargetRef.current.scrollHeight,
            behavior: 'smooth',
        });
    };

    const handleSubmit = (value: string, messageType: ChatMessageType = ChatMessageType.Message, image?: IAskImage) => {
        // Use selectedConversation safely here as the early return guarantees it exists.
        // Allow submission with text OR an image attached (issue #1504).
        if ((value.trim() === '' && !image) || selectedConversation.disabled) {
            return;
        }

        // Scroll to bottom immediately when submitting
        markCaughtUp();
        setTimeout(() => {
            scrollViewTargetRef.current?.scrollTo(0, scrollViewTargetRef.current.scrollHeight);
        }, 0);

        dispatch(editConversationInput({ id: selectedId, newInput: '' }));
        // Per-request correlation id (#1564): tag the in-flight turn so the SignalR
        // handler can drop late status events from a cancelled run that arrive after
        // this new turn has started. Generate before dispatching the local state
        // transition so activeRunId is set before any SignalR event can be processed.
        // When crypto.randomUUID is unavailable (older browsers / non-secure contexts)
        // we omit runId entirely: backend binds Ask.RunId as Guid? and will model-bind
        // 400 on any non-GUID string, and the server falls back to generating its own
        // runId. The stale-run filter degrades gracefully when activeRunId is undefined.
        const runId =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : undefined;
        // Snapshot the prompt so cancelInFlightRequest can restore it to the input
        // even if Stop is clicked before useChat.getResponse commits the user bubble.
        // Skipped while a human agent handles the chat: the backend short-circuits the AI
        // workflow then (HumanModeGate), so pretending the AI is generating would flash the
        // workflow status card, the Stop button, and a disabled input for a plain agent
        // message send (#1770). The terminal null status the controller still broadcasts is
        // a harmless no-op on an already-clear state.
        if (!agentSessionActive) {
            dispatch(updateBotResponseStatus({ chatId: selectedId, status: 'thinking', inFlightPrompt: value, runId }));
        }
        onSubmit({ value, messageType, chatId: selectedId, image, runId }).catch((error: unknown) => {
            const message = t('error.submitChat', { message: (error as Error).message });

            dispatch(
                addAlert({
                    type: AlertType.Error,
                    message,
                }),
            );
        });
    };

    // --- Render ---
    // The jump-to-bottom control has two visual modes sharing one Button: a plain circular
    // chevron when caught-up scroll has just disengaged, and an accent "N new messages" pill
    // when messages arrived below the fold. Only shape/className/label/children differ.
    const hasUnread = unreadCount > 0;
    const jumpLabel = hasUnread ? t('chatRoom.newMessages', { count: unreadCount }) : t('chatRoom.jumpToBottom');

    return (
        <div className={classes.root}>
            <div ref={scrollViewTargetRef} className={embeddedMode ? classes.scrollEmbedded : classes.scroll}>
                <div className={classes.scrollContent}>
                    <div className={classes.guidanceCard}>
                        <GuidanceCard onQuerySubmit={handleSubmit} />
                    </div>
                    <div className={classes.spacer} />
                    <div className={embeddedMode ? classes.historyEmbedded : classes.history}>
                        <ChatHistory
                            onQuerySubmit={handleSubmit}
                            onQuestionButtonClick={handleQuestionButtonClick}
                            statuses={statuses} // Pass statuses
                        />
                    </div>
                </div>
            </div>
            <div className={embeddedMode ? classes.inputEmbedded : classes.input}>
                {!shouldAutoScroll && (
                    <Button
                        icon={<ChevronDownIcon />}
                        appearance="secondary"
                        shape={hasUnread ? 'rounded' : 'circular'}
                        size="large"
                        className={
                            hasUnread
                                ? mergeClasses(classes.jumpToBottom, classes.jumpToBottomUnread)
                                : classes.jumpToBottom
                        }
                        onClick={handleJumpToBottom}
                        aria-label={jumpLabel}
                        title={jumpLabel}
                    >
                        {hasUnread && jumpLabel}
                    </Button>
                )}
                <ChatInput handleSubmit={handleSubmit} />
            </div>
        </div>
    );
};
